// T-445 — AUTO-ACCOPPIAMENTO dieta → composizione pasto a 5 slot.
//
// Tabella distillata dal file operativo reale dell'ospedale
// (`Bluecoop/File ospedale/ORDINI PASTI OK.xlsx`, foglio CARICO DIETE →
// vedi `docs/collettiva-carico-diete-mapping.md`). Ogni dieta (codice
// collettivaDiet) auto-riempie 5 slot: primo / secondo / contorno / frutta
// (LABEL di `order_catalog_rows`) + bevanda (default "Acqua"; "-" = nessuna).
//
// È la LOGICA REALE che sostituisce il corpo provvisorio "by-name" di
// `autoAssignTrittico` (collettivaTrittico.ts). La firma resta compatibile.
// Il 5° slot `bevanda` è un'aggiunta TrackEAT (l'Excel ha solo 4 colonne).
//
// Regole recepite dal foglio:
// - La dieta NON si applica uniformemente ai 4 corsi: es. il diabetico ha
//   primo/secondo diabetici ma CONTORNO ORDINARIO e frutta = MELA.
// - I MODIFICATORI (frutta cotta, omogeneizzato, carne frullata, pasta al
//   pomodoro) NON sono codici: sono override per-letto gestiti dal preposto
//   via note/scorporate. Qui c'è solo il TEMPLATE BASE di ogni dieta.
// - `null` su uno slot = quella dieta non prevede quel corso (es. astringente
//   = solo primo). `bevanda:'-'` = nessuna bevanda (digiuno/parenterale).
//
// ⚠️ 8/22 codici NON hanno un template nel foglio (diete di vincolo/consistenza
// aggiunte dai moduli reparto o dall'owner, mai presenti nella matrice piatti):
// restano a slot vuoti in attesa di decisione owner (vedi note per codice).

export interface MealComposition {
  /** LABEL di order_catalog_rows (PRIMI), o null se la dieta non prevede il corso. */
  primo: string | null;
  secondo: string | null;
  contorno: string | null;
  frutta: string | null;
  /** Bevanda: "Acqua" (default), una bevanda specifica, o "-" (nessuna). */
  bevanda: string | null;
}

const A = 'Acqua';
const NONE_MEAL: MealComposition = { primo: null, secondo: null, contorno: null, frutta: null, bevanda: A };

// dietCode → composizione base. Confidence nella colonna commento:
// [H]=alta (riga Excel netta), [M]=media (scelta tra righe candidate),
// [∅]=nessun template nel foglio (da definire con l'owner).
export const COLLETTIVA_MEAL_TEMPLATES: Readonly<Record<string, MealComposition>> = {
  // — con template Excel solido —
  ordinaria:        { primo: '1° Ordinario',            secondo: '2° Ordinario',            contorno: 'Contorno ordinario',   frutta: 'Frutta fresca', bevanda: A }, // [H] row3
  diabetica:        { primo: '1° Ordinario diabetico',  secondo: '2° Ordinario diabetico',  contorno: 'Contorno ordinario',   frutta: 'Mela',          bevanda: A }, // [H] row6 (contorno ORDINARIO, frutta MELA)
  iposodica:        { primo: '1° Ordinario iposodico',  secondo: '2° Ordinario iposodico',  contorno: 'Contorno iposodico',   frutta: 'Frutta fresca', bevanda: A }, // [H] row9
  senza_latticini:  { primo: '1° Ordinario no latticini', secondo: '2° Ordinario no latticini', contorno: 'Contorno no latticini', frutta: 'Frutta fresca', bevanda: A }, // [H] row29
  aproteica:        { primo: 'Pasta aproteica',         secondo: '2° Aproteico',            contorno: 'Contorno aproteico',   frutta: 'Frutta fresca', bevanda: A }, // [H] row39
  celiaca:          { primo: 'Pasta celiaca',           secondo: '2° Celiaco',              contorno: 'Contorno celiaco',     frutta: 'Frutta fresca', bevanda: A }, // [H] row40
  semiliquida:      { primo: 'Pastina vegetale',        secondo: '2° Similiquido',          contorno: 'Purè di patate',       frutta: 'Frutta cotta',  bevanda: A }, // [H] row12
  liquida:          { primo: 'Semolino liquido',        secondo: 'Carne frullata',          contorno: 'Verdura frullata',     frutta: 'Succo di frutta', bevanda: A }, // [H] row21
  astringente:      { primo: 'Riso in bianco',          secondo: null,                      contorno: null,                   frutta: null,            bevanda: A }, // [H] row26 (solo primo)
  frullata:         { primo: 'Pastina vegetale',        secondo: 'Carne frullata',          contorno: 'Purè di patate',       frutta: 'Frutta cotta',  bevanda: A }, // [M] row16 (alt: liquida)
  omogenizzato:     { primo: 'Pastina vegetale',        secondo: 'Omogeneizzato di carne',  contorno: 'Purè di patate',       frutta: 'Omogeneizzato di frutta', bevanda: A }, // [M] row17 (alt: row11 ordinario+omog)
  leggera_vegetale: { primo: 'Riso in bianco',          secondo: 'Pollo lesso',             contorno: 'Patate lesse',         frutta: 'Mela',          bevanda: A }, // [M] row25 (POLLO→lesso, da confermare)
  dieta_bianco:     { primo: 'Pastina con olio',        secondo: null,                      contorno: null,                   frutta: 'Frutta cotta',  bevanda: A }, // [M] row15
  idrica:           { primo: null,                       secondo: null,                      contorno: null,                   frutta: null,            bevanda: A }, // [M] row30 DIETA ① (solo acqua)

  // — bevanda esplicitamente NESSUNA —
  digiuno:          { primo: null, secondo: null, contorno: null, frutta: null, bevanda: '-' }, // [∅] digiuno per esami: niente
  parenterale:      { primo: null, secondo: null, contorno: null, frutta: null, bevanda: '-' }, // [∅] non per bocca

  // — [∅] senza template nel foglio (da definire con l'owner) —
  diabetica_iposodica: { ...NONE_MEAL }, // combinazione non presente nell'Excel (ha diab e ipo separati)
  ipocalorica:         { ...NONE_MEAL }, // solo nel foglio SALA dei moduli, non nella matrice piatti
  protetta:            { ...NONE_MEAL }, // vincolo (no crudo/insaccati) = ordinaria + note; nessun piatto proprio
  liquida_densa:       { ...NONE_MEAL }, // aggiunta owner 2026-07-08; probabile = liquida + Semolino denso + addensante
  dieta_morbida:       { ...NONE_MEAL }, // catalogo ha righe *_morbido ma il foglio non definisce la dieta
  personalizzata:      { ...NONE_MEAL }, // per definizione libera (vedi note)
};

export interface CatalogLite {
  label: string;
  category?: string;
}

/**
 * Composizione pasto per una dieta, filtrata sul catalogo REALE passato: uno slot
 * il cui piatto non è (più) a catalogo degrada a null (mai una label penzolante).
 * `bevanda` passa sempre (non è un piatto di catalogo). Codice sconosciuto →
 * tutto null + bevanda "Acqua". Deterministico, DB-free, testabile.
 */
export function mealCompositionForDiet(dietCode: string | null | undefined, catalog: CatalogLite[]): MealComposition {
  const t = dietCode ? COLLETTIVA_MEAL_TEMPLATES[dietCode] : undefined;
  if (!t) return { primo: null, secondo: null, contorno: null, frutta: null, bevanda: A };
  const inCatalog = new Set(catalog.map((c) => c.label));
  const keep = (label: string | null): string | null => (label && inCatalog.has(label) ? label : null);
  return {
    primo: keep(t.primo),
    secondo: keep(t.secondo),
    contorno: keep(t.contorno),
    frutta: keep(t.frutta),
    bevanda: t.bevanda, // la bevanda non è un piatto di catalogo → passa sempre
  };
}

/** true se la dieta ha almeno un piatto auto-accoppiato (template non vuoto). */
export function hasMealTemplate(dietCode: string | null | undefined): boolean {
  const t = dietCode ? COLLETTIVA_MEAL_TEMPLATES[dietCode] : undefined;
  return !!t && (t.primo !== null || t.secondo !== null || t.contorno !== null || t.frutta !== null);
}
